import type { ColDef, ICellRendererParams, ValueGetterParams } from 'ag-grid-enterprise'
import type { ReactElement } from 'react'
import type { Menu } from '~/components/ActionMenu'
import type { IProject, IReportedPost } from '~/types/report'

import ActionMenu from '~/components/ActionMenu'
import BadgeCellRenderer from '~/components/CellRenderers/BadgeCellRenderer'
import FileCellRenderer from '~/components/CellRenderers/FileCellRenderer'
import TextCellRenderer from '~/components/CellRenderers/TextCellRenderer'
import { actionsColDef, badgeColDef, dateColDef, textColDef } from '~/utils/table'

type TData = IProject

export interface IContext {
  handleRedirect: (path: string) => void
}

const ActionsCellRenderer = ({ context, data }: ICellRendererParams<TData, void, IContext>): ReactElement => {
  const { handleRedirect } = context

  if (data && data.original_post_id?.length) {
    const menu: Menu = {
      actions: [
        {
          action: () => handleRedirect(`/reports/${data.original_post_id}`),
          label: 'Go to original post',
        },
      ],
    }

    return <ActionMenu menu={menu} menuClassName="ASO-keyword-action-menu" placement="bottom" />
  }

  return <div />
}

const columns: ColDef[] = [
  {
    autoHeight: true,
    cellRenderer: FileCellRenderer,
    cellStyle: {
      alignItems: 'center',
      display: 'flex',
      padding: '2rem',
    },
    field: 'file_id',
    headerName: 'File',
    resizable: false,
    sortable: false,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
  },
  textColDef(TextCellRenderer, {
    field: 'original_post_id',
    headerName: 'Post ID',
    resizable: false,
    suppressMovable: true,
    suppressNavigable: true,
  }),
  textColDef(TextCellRenderer, {
    field: 'project_id',
    headerName: 'Project ID',
    resizable: false,
    suppressMovable: true,
    suppressNavigable: true,
  }),
  badgeColDef(BadgeCellRenderer, {
    field: 'status',
    filterValueGetter: ({ data }: ValueGetterParams<TData>): string | null | undefined => data?.status,
    headerName: 'Status',
    resizable: false,
    suppressMovable: true,
    suppressNavigable: true,
    valueGetter: ({
      data,
    }: ValueGetterParams<TData>): {
      data: { id: string; name: string }[]
      maxItems?: number
      type: string
    } | null => {
      if (data) {
        const { status } = data

        return {
          data: [
            {
              id: status,
              name: status,
            },
          ],
          type: 'default',
        }
      }

      return null
    },
  }),
  badgeColDef(BadgeCellRenderer, {
    field: 'type',
    filterValueGetter: ({ data }: ValueGetterParams<TData>): string | null | undefined => data?.type,
    headerName: 'Type',
    resizable: false,
    suppressMovable: true,
    suppressNavigable: true,
    valueGetter: ({
      data,
    }: ValueGetterParams<IReportedPost>): {
      data: { id: string; name: string }[]
      type: string
    } | null => {
      if (data) {
        const { type } = data

        return {
          data: [
            {
              id: type,
              name: type,
            },
          ],
          type: 'default',
        }
      }

      return null
    },
  }),
  dateColDef({
    field: 'updated_at',
    headerName: 'Update Date',
    resizable: false,
    valueGetter: ({ data }: ValueGetterParams<TData>) => data && data.updated_at && data.updated_at * 1000000,
  }),
  actionsColDef(ActionsCellRenderer, {
    field: 'actions',
  }),
]

export default columns
